import { makeAutoObservable } from 'mobx'
import { nanoid } from 'nanoid'
import { logger } from 'shared/lib'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { toastStore } from 'shared/ui'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { type IUser } from 'entities/Users/model/types'
import { UsersApi } from 'entities/Users'
import { type IParamsUpdateMemberPermissions, type IResponseUser } from 'entities/Users/api/types'
import { User } from 'entities/Users/model/User'
import { PriceInfoStore } from 'entities/Billing/ui/PriceInfo/store/PriceInfoStore'
import { BillingApi } from 'entities/Billing'
import { UpgradeMemberModal } from 'pages/settings/pages/members/ui/actions/upgradeMember/ui/UpgradeMemberModal/UpgradeMemberModal'

export class UpgradeMemberStore {
  private _modalId = nanoid()
  private _priceInfoStore: PriceInfoStore

  constructor(
    private _memberToUpgrade: IUser,
    private _entities: IEntitiesStore,
    private _params: IParamsUpdateMemberPermissions,
    private _onSuccess?: (member: IResponseUser) => void | Promise<void>
  ) {
    makeAutoObservable(this)
    this._priceInfoStore = new PriceInfoStore((count: number, cancelToken) =>
      BillingApi.getBillingSeatsPreview(
        {
          plan: this._entities.subscriptionStore.plan?.shared_inboxes_plan_id || '',
          seats: count,
          use_unassigned_seats: 0,
        },
        {
          cancelToken: cancelToken,
        }
      )
    )
    this.priceInfoStore.loadPriceData(1)
    this.openUpgradeMemberModal()
  }

  openUpgradeMemberModal = () => {
    this._modalId = nanoid()

    modalStore.addModal({
      id: this._modalId,
      width: 480,
      title: 'Upgrade',
      type: ModalTypeList.DEFAULT,
      ModalContent: UpgradeMemberModal,
      ModalContentProps: {
        store: this,
      },
      paddingContent: '0 24px 16px 24px',
      onClose: this.onCloseUpgradeMemberModal,
      primaryAction: {
        text: 'Upgrade',
        onAction: this.onUpgradeMember,
      },
    })
  }

  onCloseUpgradeMemberModal = () => {
    modalStore.removeModal(this._modalId)
  }

  onUpgradeMember = async () => {
    const memberToUpgrade = this._memberToUpgrade

    if (!memberToUpgrade || !this._params) return

    try {
      const { data } = await UsersApi.updateMember(memberToUpgrade.id, this._params)

      toastStore.add({
        type: 'success',
        title: 'Permissions updated',
      })

      if (data && memberToUpgrade instanceof User) {
        memberToUpgrade.syncOrigin(data)
        this._onSuccess?.(data)
      }

      this.onCloseUpgradeMemberModal()
    } catch (error) {
      logger.error(error)
      toastStore.add({
        type: 'error',
        title: 'Failed to update permissions. Please try again',
      })
    }
  }

  get priceInfoStore() {
    return this._priceInfoStore
  }

  get memberToUpgrade() {
    return this._memberToUpgrade
  }
}
